import { NavalConstants } from '../constants/NavalConstants';

export const INSTITUCION_SEMAR = 'SEMAR';
export const INSTITUCION_GUARDIA_NACIONAL = 'GUARDIA_NACIONAL';
export const INSTITUCION_SEDENA = 'SEDENA';

const GN_GRADOS = [
  'Guardia',
  'Cabo',
  'Sargento Segundo',
  'Sargento Primero',
  'Suboficial',
  'Inspector',
  'Segundo Subinspector',
  'Primer Subinspector',
  'Inspector Jefe',
  'Inspector General',
  'Comisario',
  'Comisario General',
  'Comisario Jefe'
];

const GN_CUERPOS = ['CAPACIDAD_OPERATIVA', 'CAPACIDAD_DE_SERVICIOS'];

const GN_ESPECIALIDADES = {
  CAPACIDAD_OPERATIVA: ['SEGURIDAD_PUBLICA'],
  CAPACIDAD_DE_SERVICIOS: ['ADMINISTRACION', 'SANIDAD']
};

const SEDENA_GRADOS = [
  'Soldado',
  'Cabo',
  'Sargento Segundo',
  'Sargento Primero',
  'Subteniente',
  'Teniente',
  'Capitán Segundo',
  'Capitán Primero',
  'Mayor',
  'Teniente Coronel',
  'Coronel',
  'General Brigadier',
  'General de Brigada',
  'General de División',
  'General Secretario de la Defensa Nacional'
];

const SEDENA_CUERPOS = ['ARMAS', 'SERVICIOS'];

const SEDENA_ESPECIALIDADES = {
  ARMAS: ['INFANTERIA', 'CABALLERIA', 'ARTILLERIA', 'BLINDADA', 'INGENIEROS', 'TRANSMISIONES'],
  SERVICIOS: [
    'INTENDENCIA',
    'SANIDAD',
    'MATERIALES_DE_GUERRA',
    'JUSTICIA_MILITAR',
    'CARTOGRAFIA',
    'ADMINISTRACION'
  ]
};

/**
 * Catálogo de instituciones. Los emblemas son abstractos (sin heráldica oficial)
 * para evitar cualquier uso indebido de imagen institucional.
 */
export const INSTITUCIONES = [
  {
    id: INSTITUCION_SEMAR,
    key: 'semar',
    nombre: 'SEMAR',
    nombreLargo: 'Secretaría de Marina Armada de México',
    subtitulo: 'Secretaría de Marina\nArmada de México',
    emoji: '⚓',
    accent: '#c5a059',
    accentLight: '#dbb96a',
    gradient: ['#8f6f35', '#c5a059'],
    terminology: {
      hierarchyLabel: 'Jerarquía',
      groupLabel: 'Cuerpo o Servicio',
      specialtyLabel: 'Especialidad',
      groupPrompt: 'Selecciona tu jerarquía, cuerpo o servicio y especialidad para personalizar tu contenido.',
      groupRequiredPrompt: 'Selecciona primero tu cuerpo o servicio',
      specialtyPlaceholder: 'Selecciona una especialidad'
    },
    catalog: {
      grados: NavalConstants.GRADOS,
      cuerpos: NavalConstants.CUERPOS_Y_SERVICIOS,
      especialidades: NavalConstants.ESPECIALIDADES
    }
  },
  {
    id: INSTITUCION_GUARDIA_NACIONAL,
    key: 'guardia_nacional',
    nombre: 'Guardia Nacional',
    nombreLargo: 'Guardia Nacional de México',
    subtitulo: 'Guardia Nacional\nMéxico',
    emoji: '🛡️',
    accent: '#3f7a52',
    accentLight: '#8fc9a3',
    gradient: ['#3f7a52', '#e6e9ec'],
    terminology: {
      hierarchyLabel: 'Jerarquía',
      groupLabel: 'Capacidad',
      specialtyLabel: 'Especialidad',
      groupPrompt: 'Selecciona tu jerarquía, capacidad y especialidad para personalizar tu contenido.',
      groupRequiredPrompt: 'Selecciona primero tu capacidad',
      specialtyPlaceholder: 'Selecciona una especialidad'
    },
    catalog: {
      grados: GN_GRADOS,
      cuerpos: GN_CUERPOS,
      especialidades: GN_ESPECIALIDADES
    }
  },
  {
    id: INSTITUCION_SEDENA,
    key: 'sedena',
    nombre: 'SEDENA',
    nombreLargo: 'Secretaría de la Defensa Nacional',
    subtitulo: 'Secretaría de la\nDefensa Nacional',
    emoji: '⭐',
    accent: '#4e6b3a',
    accentLight: '#c9b37e',
    gradient: ['#4e6b3a', '#c9b37e'],
    terminology: {
      hierarchyLabel: 'Jerarquía',
      groupLabel: 'Cuerpo / Servicio',
      specialtyLabel: 'Especialidad',
      groupPrompt: 'Selecciona tu jerarquía, cuerpo o servicio y especialidad para personalizar tu contenido.',
      groupRequiredPrompt: 'Selecciona primero tu cuerpo o servicio',
      specialtyPlaceholder: 'Selecciona una especialidad'
    },
    catalog: {
      grados: SEDENA_GRADOS,
      cuerpos: SEDENA_CUERPOS,
      especialidades: SEDENA_ESPECIALIDADES
    }
  }
];

export const SEMAR_FALLBACK_CATALOG = {
  grados: NavalConstants.GRADOS,
  cuerpos: NavalConstants.CUERPOS_Y_SERVICIOS,
  especialidades: NavalConstants.ESPECIALIDADES
};

/**
 * Devuelve la institución canónica o null cuando el valor no es reconocible.
 */
export function normalizeInstitucion(value) {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim().toLowerCase();
  if (!raw) return null;
  if (raw.includes('guardia') || raw === 'gn') return INSTITUCION_GUARDIA_NACIONAL;
  if (
    raw.includes('sedena') ||
    raw.includes('ejercito') ||
    raw.includes('ejército') ||
    raw.includes('defensa nacional')
  ) {
    return INSTITUCION_SEDENA;
  }
  if (raw.includes('marina') || raw === 'semar') return INSTITUCION_SEMAR;
  return null;
}

/**
 * Institución para efectos de contenido. SEMAR es el valor histórico por defecto,
 * por lo que un valor desconocido conserva la ruta raíz intacta.
 */
export function resolveInstitucion(value) {
  return normalizeInstitucion(value) || INSTITUCION_SEMAR;
}

export function getInstitution(value) {
  const id = resolveInstitucion(value);
  return INSTITUCIONES.find((inst) => inst.id === id) || INSTITUCIONES[0];
}

export function getInstitutionKey(value) {
  return getInstitution(value).key;
}

export function isSemar(value) {
  return resolveInstitucion(value) === INSTITUCION_SEMAR;
}

export function getInstitutionLabel(value, { long = false } = {}) {
  const inst = getInstitution(value);
  return long ? inst.nombreLargo : inst.nombre;
}

/** SEMAR conserva la raíz histórica; GN/SEDENA viven bajo content/{institucion}. */
export function getContentPrefix(value) {
  const key = getInstitutionKey(value);
  return key === 'semar' ? '' : `content/${key}`;
}

export function getContentPath(value, node = '') {
  const prefix = getContentPrefix(value);
  const normalizedNode = String(node || '').replace(/^\/+/, '');
  if (!prefix) return normalizedNode;
  return normalizedNode ? `${prefix}/${normalizedNode}` : prefix;
}

export function getQuizBankVersionPaths(value) {
  if (!isSemar(value)) return [];
  return [
    'cache_control/areas_version',
    'cache_control/temas_version',
    'cache_control/preguntas_updated'
  ];
}

/**
 * Rutas de especialidades en orden de precedencia. Replica la búsqueda de
 * Android: perfil exacto → cuerpo/especialidad → cuerpo → global.
 */
export function getSpecialtyPaths(value, hierarchyCode, corpsCode, specialtyCode) {
  const paths = [];
  const push = (node) => {
    const path = getContentPath(value, node);
    if (!paths.includes(path)) paths.push(path);
  };

  if (hierarchyCode && corpsCode && specialtyCode) {
    push(`especialidades_temas/${hierarchyCode}/${corpsCode}/${specialtyCode}/temas`);
  }
  if (corpsCode && specialtyCode) {
    push(`especialidades_temas/${corpsCode}/${specialtyCode}/temas`);
  }
  if (corpsCode) {
    push(`especialidades_temas/${corpsCode}/temas`);
  }
  // SEDENA organiza el tronco como TODOS/{cuerpo}/TODOS/temas.
  if (corpsCode && specialtyCode) {
    push(`especialidades_temas/TODOS/${corpsCode}/${specialtyCode}/temas`);
  }
  if (corpsCode) {
    push(`especialidades_temas/TODOS/${corpsCode}/TODOS/temas`);
    push(`especialidades_temas/TODOS/${corpsCode}/temas`);
  }
  push('especialidades_temas/TODOS/TODOS/TODOS/temas');
  return paths;
}

export function getCatalog(value, { cuerpo = '' } = {}) {
  const inst = getInstitution(value);
  const catalog = inst.catalog || SEMAR_FALLBACK_CATALOG;
  const especialidades = catalog.especialidades || {};
  return {
    grados: catalog.grados || [],
    cuerpos: catalog.cuerpos || [],
    especialidades,
    especialidadesDe: (cuerpoSeleccionado = cuerpo) =>
      (especialidades[cuerpoSeleccionado] || []).slice()
  };
}

export function getExamConfig(value) {
  switch (resolveInstitucion(value)) {
    case INSTITUCION_GUARDIA_NACIONAL:
      return { totalQuestions: 400, durationSeconds: 120 * 60, passPercent: 60 };
    case INSTITUCION_SEDENA:
      return { totalQuestions: 200, durationSeconds: 120 * 60, passPercent: 60 };
    default:
      return { totalQuestions: 100, durationSeconds: 80 * 60, passPercent: null };
  }
}

/**
 * Un perfil está completo cuando el onboarding ya se confirmó o cuando la app
 * móvil dejó institución y jerarquía en Firestore. Los usuarios existentes no
 * repiten el onboarding en la web.
 */
export function isProfileComplete(profile) {
  if (!profile || typeof profile !== 'object') return false;
  if (profile.onboardingCompleted === true) return true;
  const institucion = normalizeInstitucion(profile.institucion);
  const grado = profile.grado || profile.jerarquia || profile.hierarchy || '';
  return Boolean(institucion && String(grado).trim());
}

export function normalizeRank(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

const SEMAR_RANK_TOKENS = [
  'MARINERO', 'MRO', 'TERCER MAESTRE', '3RM', 'SEGUNDO MAESTRE', '2DM',
  'PRIMER MAESTRE', '1RM', 'TENIENTE DE CORBETA', 'TENIENTE CORBETA', 'TTC',
  'TENIENTE DE FRAGATA', 'TENIENTE FRAGATA', 'TTF', 'TENIENTE DE NAVIO',
  'TENIENTE NAVIO', 'TTN', 'CAPITAN DE CORBETA', 'CAPITAN CORBETA', 'CAP CORB'
];

const GUARDIA_NACIONAL_RANK_TOKENS = [
  'GUARDIA NACIONAL', 'GUARDIA', 'INSPECTOR', 'SUBINSPECTOR', 'COMISARIO'
];

const SEDENA_RANK_TOKENS = [
  'SOLDADO', 'SUBTENIENTE', 'CAPITAN SEGUNDO', 'CAPITAN PRIMERO', 'MAYOR',
  'TENIENTE CORONEL', 'CORONEL', 'GENERAL BRIGADIER', 'GENERAL DE BRIGADA',
  'GENERAL DE DIVISION', 'GENERAL SECRETARIO'
];

/**
 * Inferencia conservadora para perfiles heredados sin `institucion`.
 * Los grados ambiguos (Cabo, Suboficial) se resuelven como SEMAR, que es la
 * institución histórica y no debe alterarse.
 */
export function inferInstitucionFromRank(value) {
  const rank = normalizeRank(value);
  if (!rank) return null;
  if (GUARDIA_NACIONAL_RANK_TOKENS.some((token) => rank.includes(token))) {
    return INSTITUCION_GUARDIA_NACIONAL;
  }
  if (SEDENA_RANK_TOKENS.some((token) => rank.includes(token))) {
    return INSTITUCION_SEDENA;
  }
  if (SEMAR_RANK_TOKENS.some((token) => rank.includes(token))) {
    return INSTITUCION_SEMAR;
  }
  return null;
}

export default {
  INSTITUCIONES,
  INSTITUCION_SEMAR,
  INSTITUCION_GUARDIA_NACIONAL,
  INSTITUCION_SEDENA,
  normalizeInstitucion,
  resolveInstitucion,
  getInstitution,
  getInstitutionKey,
  getInstitutionLabel,
  isSemar,
  getContentPrefix,
  getContentPath,
  getQuizBankVersionPaths,
  getSpecialtyPaths,
  getCatalog,
  getExamConfig,
  isProfileComplete,
  normalizeRank
};
